import type { PropsWithChildren } from 'react';
import { useMemo } from 'react';
import { createContext, useContextSelector } from 'use-context-selector';
import type { AssetDndZone } from '@frameio/assets/operations';
import { DndZoneContextProvider } from '@frameio/assets/operations';

export type FolderUploadsContextValue = {
  dndZone?: AssetDndZone;
  transfersDnd?: {
    hoveredId: string | null;
    setHoveredId: (assetId: string | null) => void;
    onTransferDrop: (assetId: string, dataTransfer: DataTransfer) => void;
  };
};

type Props = PropsWithChildren<FolderUploadsContextValue>;

const FolderUploadsContext = createContext<FolderUploadsContextValue>({
  dndZone: undefined,
  transfersDnd: undefined,
});

/**
 * The ProjectTreeUploadsContextProvider provides the transfer dnd context
 * for folders in the ProjectTree.
 */
export function ProjectTreeUploadsContextProvider({
  children,
  dndZone,
  transfersDnd,
}: Props) {
  const value = useMemo<FolderUploadsContextValue>(
    () => ({
      dndZone,
      transfersDnd,
    }),
    [dndZone, transfersDnd]
  );

  return (
    <FolderUploadsContext.Provider value={value}>
      <DndZoneContextProvider zone={dndZone ?? null}>
        {children}
      </DndZoneContextProvider>
    </FolderUploadsContext.Provider>
  );
}

export const useTransfersDnd = () =>
  useContextSelector(FolderUploadsContext, ({ transfersDnd }) => transfersDnd);

export const useIsFolderUploadTarget = (assetId: string) =>
  useContextSelector(
    FolderUploadsContext,
    ({ transfersDnd }) => transfersDnd?.hoveredId === assetId
  );
